import React, { useEffect, useState } from 'react';
import { X, Loader2, Sparkles, RefreshCcw, CheckSquare, Square, Info, TableProperties } from 'lucide-react';
import ERDPreviewModal from './ERDPreviewModal';


interface ColumnSchema {
    name: string;
    type: string;
    tag?: string;
    description?: string;
}

interface ProcessedSchema {
    [table: string]: ColumnSchema[];
}

interface SmartMetadataModalProps {
    dataSource: {
        id: string;
        collectionName: string;
        description?: string;
        createdBy: string;
        createdAt: string;
        tenantId: string;
        provider: string;
        isActive: boolean;
    };
    onClose: () => void;
}
const chartCode = `erDiagram
  users {
    uuid user_id
    text first_name
    text last_name
    text email
    text phone
    uuid role_id
    uuid department_id
    boolean status
    timestamp created_on
    timestamp last_login
    text username
    text password_hash
    date dob
    int gender
    text profile_picture
    text employee_code
    text country
    text language
    text timezone
    text preferred_currency
  }

  companies {
    uuid company_id
    text company_name
    text industry
    text domain
    text email
    text phone
    text address
    text city
    text state
    text zip_code
    text country
    text website
    timestamp created_on
    text subscription_plan
    text billing_status
    text timezone
    text logo_url
    boolean is_active
    uuid primary_contact
    text notes
  }

  projects {
    uuid project_id
    text title
    text description
    text status
    date start_date
    date end_date
    numeric budget
    uuid manager_id
    uuid client_id
    int team_size
    text priority
    text project_type
    int milestone_count
    float completion_percentage
    text risk_level
    text deliverables
    uuid assigned_department
    text notes
    timestamp created_on
    timestamp last_updated
  }

  tasks {
    uuid task_id
    uuid project_id
    text title
    text description
    uuid assigned_to
    timestamp due_date
    text status
    text priority
    float estimated_hours
    float actual_hours
    uuid created_by
    timestamp created_on
    timestamp last_updated
    text comments
    text attachment_url
    uuid dependencies
    text tags
    float completion_percentage
    uuid reviewer_id
    text stage
  }

  timesheets {
    uuid entry_id
    uuid user_id
    uuid project_id
    uuid task_id
    date date
    timestamp start_time
    timestamp end_time
    float hours_logged
    text activity_description
    text approval_status
    uuid approved_by
    timestamp submitted_on
    text comments
    boolean is_billable
    numeric rate_per_hour
    numeric total_cost
    uuid invoice_id
    text rejection_reason
    timestamp last_updated
    uuid submitted_by
  }

  invoices {
    uuid invoice_id
    text invoice_number
    uuid client_id
    uuid project_id
    date issue_date
    date due_date
    numeric total_amount
    float tax_percentage
    numeric discount
    numeric final_amount
    text status
    text currency
    text notes
    uuid created_by
    timestamp created_on
    uuid approved_by
    timestamp approved_on
    text payment_terms
    text pdf_url
    text tags
  }

  payments {
    uuid payment_id
    uuid invoice_id
    uuid client_id
    timestamp payment_date
    numeric amount_paid
    text payment_method
    text transaction_id
    text status
    text currency
    uuid received_by
    text payment_reference
    text bank_name
    text account_number
    text routing_number
    text notes
    text receipt_url
    boolean is_partial
    numeric partial_amount_remaining
    timestamp last_updated
    timestamp created_on
  }

  clients {
    uuid client_id
    text company_name
    text contact_name
    text email
    text phone
    text industry
    text address
    text city
    text state
    text zip
    text country
    text website
    text preferred_currency
    text timezone
    text status
    timestamp created_on
    uuid account_manager
    text billing_cycle
    date last_invoice_date
    text notes
  }

  departments {
    uuid department_id
    text department_name
    uuid manager_id
    text location
    text extension
    text email
    text phone
    int headcount
    numeric budget
    text status
    timestamp created_on
    timestamp last_updated
    uuid parent_department
    int floor_number
    boolean is_remote
    text region
    text notes
    uuid team_leads
    text available_roles
    int active_projects
  }

  documents {
    uuid document_id
    text title
    text description
    uuid uploaded_by
    timestamp upload_date
    text file_name
    text file_path
    int file_size
    text mime_type
    text document_type
    uuid related_project_id
    uuid related_client_id
    text status
    text tags
    uuid shared_with
    text access_level
    text version
    timestamp last_modified
    boolean is_encrypted
    boolean is_archived
  }

  users ||--o{ timesheets : logs
  users ||--o{ tasks : assigned
  users ||--o{ documents : uploads
  users ||--|| departments : belongs_to
  users ||--|| companies : works_for

  projects ||--o{ tasks : contains
  projects ||--o{ timesheets : has
  projects ||--|| clients : requested_by
  projects ||--|| departments : handled_by
  projects ||--o{ invoices : billed_by

  tasks ||--o{ timesheets : tracked_by

  invoices ||--|{ payments : paid_by
  invoices ||--|| clients : issued_to

  clients ||--o{ payments : makes

  departments ||--o{ users : manages
  departments ||--o{ projects : oversees

  documents ||--|| projects : related_to
  documents ||--|| clients : attached_to

`;





const SmartMetadataModal: React.FC<SmartMetadataModalProps> = ({ dataSource, onClose }) => {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [tables, setTables] = useState<string[]>([]);
    const [selectedTables, setSelectedTables] = useState<string[]>([]);
    const [columns, setColumns] = useState<Record<string, ColumnSchema[]>>({});
    const [processedSchema, setProcessedSchema] = useState<ProcessedSchema>({});
    const [instructions, setInstructions] = useState<string>('');

    const [ShowErdModal, setShowErdModal] = useState(false);


    const loadMetadata = async () => {
        setLoading(true);
        setTimeout(() => {
            const sampleTables = ['users', 'companies', 'projects', 'tasks', 'timesheets', 'invoices', 'payments', 'clients', 'documents', 'departments'];
            const sampleColumns = {
                "users": [
                    {
                        "name": "user_id",
                        "type": "uuid",
                        "description": "Unique identifier"
                    },
                    {
                        "name": "first_name",
                        "type": "text",
                        "description": "Full name or label"
                    },
                    {
                        "name": "last_name",
                        "type": "text",
                        "description": "Full name or label"
                    },
                    {
                        "name": "email",
                        "type": "text",
                        "description": "Email address"
                    },
                    {
                        "name": "phone",
                        "type": "text",
                        "description": "Contact phone number"
                    },
                    {
                        "name": "role_id",
                        "type": "uuid",
                        "description": "Unique identifier"
                    },
                    {
                        "name": "department_id",
                        "type": "uuid",
                        "description": "Unique identifier"
                    },
                    {
                        "name": "status",
                        "type": "boolean",
                        "description": "Current status flag"
                    },
                    {
                        "name": "created_on",
                        "type": "timestamp",
                        "description": "Timestamp when the record was created"
                    },
                    {
                        "name": "last_login",
                        "type": "timestamp",
                        "description": "Timestamp of last activity"
                    },
                    {
                        "name": "username",
                        "type": "text",
                        "description": "Full name or label"
                    },
                    {
                        "name": "password_hash",
                        "type": "text",
                        "description": "Encrypted or hashed value"
                    },
                    {
                        "name": "dob",
                        "type": "date",
                        "description": "Relevant date"
                    },
                    {
                        "name": "gender",
                        "type": "int",
                        "description": "Gender indicator"
                    },
                    {
                        "name": "profile_picture",
                        "type": "text",
                        "description": "Image or file reference"
                    },
                    {
                        "name": "employee_code",
                        "type": "text",
                        "description": "Employee or organization-specific code"
                    },
                    {
                        "name": "country",
                        "type": "text",
                        "description": "General data field"
                    },
                    {
                        "name": "language",
                        "type": "text",
                        "description": "Preferred language"
                    },
                    {
                        "name": "timezone",
                        "type": "text",
                        "description": "General data field"
                    },
                    {
                        "name": "preferred_currency",
                        "type": "text",
                        "description": "Preferred currency type"
                    }
                ],
                "companies": [
                    {
                        "name": "company_id",
                        "type": "uuid",
                        "description": "Unique identifier"
                    },
                    {
                        "name": "company_name",
                        "type": "text",
                        "description": "Full name or label"
                    },
                    {
                        "name": "industry",
                        "type": "text",
                        "description": "Business sector"
                    },
                    {
                        "name": "domain",
                        "type": "text",
                        "description": "Internet domain or organization scope"
                    },
                    {
                        "name": "email",
                        "type": "text",
                        "description": "Email address"
                    },
                    {
                        "name": "phone",
                        "type": "text",
                        "description": "Contact phone number"
                    },
                    {
                        "name": "address",
                        "type": "text",
                        "description": "Location or address information"
                    },
                    {
                        "name": "city",
                        "type": "text",
                        "description": "General data field"
                    },
                    {
                        "name": "state",
                        "type": "text",
                        "description": "General data field"
                    },
                    {
                        "name": "zip_code",
                        "type": "text",
                        "description": "Employee or organization-specific code"
                    },
                    {
                        "name": "country",
                        "type": "text",
                        "description": "General data field"
                    },
                    {
                        "name": "website",
                        "type": "text",
                        "description": "Official website URL"
                    },
                    {
                        "name": "created_on",
                        "type": "timestamp",
                        "description": "Timestamp when the record was created"
                    },
                    {
                        "name": "subscription_plan",
                        "type": "text",
                        "description": "General data field"
                    },
                    {
                        "name": "billing_status",
                        "type": "text",
                        "description": "Current status flag"
                    },
                    {
                        "name": "timezone",
                        "type": "text",
                        "description": "General data field"
                    },
                    {
                        "name": "logo_url",
                        "type": "text",
                        "description": "Image or file reference"
                    },
                    {
                        "name": "is_active",
                        "type": "boolean",
                        "description": "General data field"
                    },
                    {
                        "name": "primary_contact",
                        "type": "uuid",
                        "description": "General data field"
                    },
                    {
                        "name": "notes",
                        "type": "text",
                        "description": "Additional information or comments"
                    }
                ],
                "projects": [
                    { "name": "project_id", "type": "uuid", "description": "Unique identifier for the project" },
                    { "name": "title", "type": "text", "description": "Name or title of the project" },
                    { "name": "description", "type": "text", "description": "Detailed description of the project" },
                    { "name": "status", "type": "text", "description": "Current status (e.g., Active, Completed)" },
                    { "name": "start_date", "type": "date", "description": "Date when the project starts" },
                    { "name": "end_date", "type": "date", "description": "Projected or actual completion date" },
                    { "name": "budget", "type": "numeric", "description": "Allocated budget for the project" },
                    { "name": "manager_id", "type": "uuid", "description": "ID of the project manager" },
                    { "name": "client_id", "type": "uuid", "description": "ID of the client or stakeholder" },
                    { "name": "team_size", "type": "int", "description": "Number of people working on the project" },
                    { "name": "priority", "type": "text", "description": "Priority level of the project" },
                    { "name": "project_type", "type": "text", "description": "Type/category of the project" },
                    { "name": "milestone_count", "type": "int", "description": "Total number of milestones" },
                    { "name": "completion_percentage", "type": "float", "description": "Overall project completion %" },
                    { "name": "risk_level", "type": "text", "description": "Assessed risk level (Low, Medium, High)" },
                    { "name": "deliverables", "type": "text", "description": "Expected deliverables from the project" },
                    { "name": "assigned_department", "type": "uuid", "description": "Department responsible for project" },
                    { "name": "notes", "type": "text", "description": "Additional notes or remarks" },
                    { "name": "created_on", "type": "timestamp", "description": "Timestamp when the project was created" },
                    { "name": "last_updated", "type": "timestamp", "description": "Last update timestamp" }
                ],
                "tasks": [
                    { "name": "task_id", "type": "uuid", "description": "Unique identifier for the task" },
                    { "name": "project_id", "type": "uuid", "description": "Project to which this task belongs" },
                    { "name": "title", "type": "text", "description": "Short title of the task" },
                    { "name": "description", "type": "text", "description": "Detailed description of the task" },
                    { "name": "assigned_to", "type": "uuid", "description": "User ID of the assigned person" },
                    { "name": "due_date", "type": "timestamp", "description": "Due date for task completion" },
                    { "name": "status", "type": "text", "description": "Current status (e.g., In Progress, Done)" },
                    { "name": "priority", "type": "text", "description": "Priority level of the task" },
                    { "name": "estimated_hours", "type": "float", "description": "Estimated effort in hours" },
                    { "name": "actual_hours", "type": "float", "description": "Actual time spent on the task" },
                    { "name": "created_by", "type": "uuid", "description": "User who created the task" },
                    { "name": "created_on", "type": "timestamp", "description": "Timestamp when task was created" },
                    { "name": "last_updated", "type": "timestamp", "description": "Last update timestamp" },
                    { "name": "comments", "type": "text", "description": "Comments or notes related to the task" },
                    { "name": "attachment_url", "type": "text", "description": "URL to related attachments" },
                    { "name": "dependencies", "type": "uuid[]", "description": "Task IDs this task depends on" },
                    { "name": "tags", "type": "text[]", "description": "Tags or labels associated with the task" },
                    { "name": "completion_percentage", "type": "float", "description": "Task completion in %" },
                    { "name": "reviewer_id", "type": "uuid", "description": "User ID responsible for reviewing" },
                    { "name": "stage", "type": "text", "description": "Current workflow stage of the task" }
                ],
                "timesheets": [
                    { "name": "entry_id", "type": "uuid", "description": "Unique identifier for the timesheet entry" },
                    { "name": "user_id", "type": "uuid", "description": "User who submitted the timesheet" },
                    { "name": "project_id", "type": "uuid", "description": "Associated project ID" },
                    { "name": "task_id", "type": "uuid", "description": "Associated task ID" },
                    { "name": "date", "type": "date", "description": "Date of the timesheet entry" },
                    { "name": "start_time", "type": "timestamp", "description": "Start time of the logged activity" },
                    { "name": "end_time", "type": "timestamp", "description": "End time of the logged activity" },
                    { "name": "hours_logged", "type": "float", "description": "Number of hours worked" },
                    { "name": "activity_description", "type": "text", "description": "Description of the work performed" },
                    { "name": "approval_status", "type": "text", "description": "Approval status of the entry" },
                    { "name": "approved_by", "type": "uuid", "description": "User ID who approved the entry" },
                    { "name": "submitted_on", "type": "timestamp", "description": "Submission date of the entry" },
                    { "name": "comments", "type": "text", "description": "Comments or feedback on the entry" },
                    { "name": "is_billable", "type": "boolean", "description": "Flag indicating if the entry is billable" },
                    { "name": "rate_per_hour", "type": "numeric", "description": "Hourly billing rate" },
                    { "name": "total_cost", "type": "numeric", "description": "Total calculated cost for the entry" },
                    { "name": "invoice_id", "type": "uuid", "description": "Associated invoice ID, if any" },
                    { "name": "rejection_reason", "type": "text", "description": "Reason for rejection if not approved" },
                    { "name": "last_updated", "type": "timestamp", "description": "Timestamp of last update" },
                    { "name": "submitted_by", "type": "uuid", "description": "User ID who submitted the entry" }
                ],
                "invoices": [
                    { "name": "invoice_id", "type": "uuid", "description": "Unique identifier for the invoice" },
                    { "name": "invoice_number", "type": "text", "description": "Human-readable invoice number" },
                    { "name": "client_id", "type": "uuid", "description": "Client associated with the invoice" },
                    { "name": "project_id", "type": "uuid", "description": "Project billed in the invoice" },
                    { "name": "issue_date", "type": "date", "description": "Date the invoice was issued" },
                    { "name": "due_date", "type": "date", "description": "Payment due date" },
                    { "name": "total_amount", "type": "numeric", "description": "Total before tax and discount" },
                    { "name": "tax_percentage", "type": "float", "description": "Tax percentage applied" },
                    { "name": "discount", "type": "numeric", "description": "Discount amount applied" },
                    { "name": "final_amount", "type": "numeric", "description": "Total amount after tax and discount" },
                    { "name": "status", "type": "text", "description": "Invoice status (e.g., Paid, Pending)" },
                    { "name": "currency", "type": "text", "description": "Currency used in the invoice" },
                    { "name": "notes", "type": "text", "description": "Additional notes or terms" },
                    { "name": "created_by", "type": "uuid", "description": "User who created the invoice" },
                    { "name": "created_on", "type": "timestamp", "description": "Invoice creation timestamp" },
                    { "name": "approved_by", "type": "uuid", "description": "User who approved the invoice" },
                    { "name": "approved_on", "type": "timestamp", "description": "Date the invoice was approved" },
                    { "name": "payment_terms", "type": "text", "description": "Agreed payment terms" },
                    { "name": "pdf_url", "type": "text", "description": "URL to the invoice PDF" },
                    { "name": "tags", "type": "text[]", "description": "Tags or labels for categorization" }
                ],
                "payments": [
                    { "name": "payment_id", "type": "uuid", "description": "Unique identifier for the payment" },
                    { "name": "invoice_id", "type": "uuid", "description": "Associated invoice ID" },
                    { "name": "client_id", "type": "uuid", "description": "Client who made the payment" },
                    { "name": "payment_date", "type": "timestamp", "description": "Date the payment was received" },
                    { "name": "amount_paid", "type": "numeric", "description": "Total amount paid" },
                    { "name": "payment_method", "type": "text", "description": "Method of payment (e.g., Bank, Card)" },
                    { "name": "transaction_id", "type": "text", "description": "Transaction reference ID" },
                    { "name": "status", "type": "text", "description": "Payment status" },
                    { "name": "currency", "type": "text", "description": "Currency used in the transaction" },
                    { "name": "received_by", "type": "uuid", "description": "User who received the payment" },
                    { "name": "payment_reference", "type": "text", "description": "External payment reference" },
                    { "name": "bank_name", "type": "text", "description": "Name of the receiving bank" },
                    { "name": "account_number", "type": "text", "description": "Account number used" },
                    { "name": "routing_number", "type": "text", "description": "Bank routing number" },
                    { "name": "notes", "type": "text", "description": "Additional payment notes" },
                    { "name": "receipt_url", "type": "text", "description": "URL to payment receipt" },
                    { "name": "is_partial", "type": "boolean", "description": "Indicates partial payment" },
                    { "name": "partial_amount_remaining", "type": "numeric", "description": "Unpaid amount if partial" },
                    { "name": "last_updated", "type": "timestamp", "description": "Last update timestamp" },
                    { "name": "created_on", "type": "timestamp", "description": "Payment creation timestamp" }
                ],
                "clients": [
                    { "name": "client_id", "type": "uuid", "description": "Unique identifier for the client" },
                    { "name": "company_name", "type": "text", "description": "Client company name" },
                    { "name": "contact_name", "type": "text", "description": "Primary contact person" },
                    { "name": "email", "type": "text", "description": "Contact email address" },
                    { "name": "phone", "type": "text", "description": "Contact phone number" },
                    { "name": "industry", "type": "text", "description": "Industry the client operates in" },
                    { "name": "address", "type": "text", "description": "Street address" },
                    { "name": "city", "type": "text", "description": "City location" },
                    { "name": "state", "type": "text", "description": "State or province" },
                    { "name": "zip", "type": "text", "description": "ZIP or postal code" },
                    { "name": "country", "type": "text", "description": "Country of residence" },
                    { "name": "website", "type": "text", "description": "Client website URL" },
                    { "name": "preferred_currency", "type": "text", "description": "Default currency for transactions" },
                    { "name": "timezone", "type": "text", "description": "Time zone for client communications" },
                    { "name": "status", "type": "text", "description": "Current status (Active, Inactive)" },
                    { "name": "created_on", "type": "timestamp", "description": "Date the client was added" },
                    { "name": "account_manager", "type": "uuid", "description": "User responsible for client" },
                    { "name": "billing_cycle", "type": "text", "description": "Preferred billing cycle (e.g., Monthly)" },
                    { "name": "last_invoice_date", "type": "date", "description": "Date of the last invoice issued" },
                    { "name": "notes", "type": "text", "description": "Internal notes about the client" }
                ],
                "departments": [
                    { "name": "department_id", "type": "uuid", "description": "Unique identifier for the department" },
                    { "name": "department_name", "type": "text", "description": "Name of the department" },
                    { "name": "manager_id", "type": "uuid", "description": "User ID of the department manager" },
                    { "name": "location", "type": "text", "description": "Location of the department office" },
                    { "name": "extension", "type": "text", "description": "Phone extension for the department" },
                    { "name": "email", "type": "text", "description": "Departmental contact email address" },
                    { "name": "phone", "type": "text", "description": "Phone number of the department" },
                    { "name": "headcount", "type": "int", "description": "Number of employees in the department" },
                    { "name": "budget", "type": "numeric", "description": "Budget allocated to the department" },
                    { "name": "status", "type": "text", "description": "Current operational status of the department" },
                    { "name": "created_on", "type": "timestamp", "description": "Creation date of the department" },
                    { "name": "last_updated", "type": "timestamp", "description": "Last time the department info was updated" },
                    { "name": "parent_department", "type": "uuid", "description": "ID of the parent department if hierarchical" },
                    { "name": "floor_number", "type": "int", "description": "Floor on which the department is located" },
                    { "name": "is_remote", "type": "boolean", "description": "Indicates if the department operates remotely" },
                    { "name": "region", "type": "text", "description": "Geographic region for the department" },
                    { "name": "notes", "type": "text", "description": "Additional notes related to the department" },
                    { "name": "team_leads", "type": "uuid[]", "description": "List of team leads under this department" },
                    { "name": "available_roles", "type": "text[]", "description": "Roles available or managed by this department" },
                    { "name": "active_projects", "type": "int", "description": "Count of currently active projects" }
                ],
                "documents": [
                    { "name": "document_id", "type": "uuid", "description": "Unique identifier for the document" },
                    { "name": "title", "type": "text", "description": "Title or name of the document" },
                    { "name": "description", "type": "text", "description": "Brief explanation of the document's content" },
                    { "name": "uploaded_by", "type": "uuid", "description": "User ID who uploaded the document" },
                    { "name": "upload_date", "type": "timestamp", "description": "Timestamp when the document was uploaded" },
                    { "name": "file_name", "type": "text", "description": "Original file name" },
                    { "name": "file_path", "type": "text", "description": "Storage path of the document file" },
                    { "name": "file_size", "type": "int", "description": "Size of the file in bytes" },
                    { "name": "mime_type", "type": "text", "description": "MIME type of the document (e.g., application/pdf)" },
                    { "name": "document_type", "type": "text", "description": "Category or type of document (e.g., invoice, report)" },
                    { "name": "related_project_id", "type": "uuid", "description": "ID of the associated project" },
                    { "name": "related_client_id", "type": "uuid", "description": "ID of the related client" },
                    { "name": "status", "type": "text", "description": "Status of the document (e.g., active, archived)" },
                    { "name": "tags", "type": "text[]", "description": "Tags or keywords associated with the document" },
                    { "name": "shared_with", "type": "uuid[]", "description": "List of user IDs the document is shared with" },
                    { "name": "access_level", "type": "text", "description": "Level of access granted to viewers" },
                    { "name": "version", "type": "text", "description": "Document version identifier" },
                    { "name": "last_modified", "type": "timestamp", "description": "Last modified timestamp" },
                    { "name": "is_encrypted", "type": "boolean", "description": "Whether the file is encrypted" },
                    { "name": "is_archived", "type": "boolean", "description": "Flag indicating if the document is archived" }
                ]
            };
            setTables(sampleTables);
            setColumns(sampleColumns);
            setStep(2);
            setLoading(false);
        }, 1000);
    };

    const processMetadata = async () => {
        setLoading(true);
        setTimeout(() => {
            const processed: ProcessedSchema = {};
            selectedTables.forEach(table => {
                processed[table] = columns[table].map(col => ({
                    ...col,
                    tag: col.name.includes('id') ? 'Identifier' : col.name.includes('date') ? 'Time' : 'Metric',
                    // description: `Auto description for ${col.name}`
                }));
            });
            setProcessedSchema(processed);
            setStep(3);
            setLoading(false);
        }, 1200);
    };

    const toggleTable = (table: string) => {
        setSelectedTables(prev =>
            prev.includes(table) ? prev.filter(t => t !== table) : [...prev, table]
        );
    };

    const toggleSelectAll = () => {
        setSelectedTables(prev => (prev.length === tables.length ? [] : [...tables]));
    };

    const updateColumnTag = (table: string, colName: string, field: keyof ColumnSchema, value: string) => {
        setProcessedSchema(prev => ({
            ...prev,
            [table]: prev[table].map(col =>
                col.name === colName ? { ...col, [field]: value } : col
            )
        }));
    };

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-neutral-900 w-full max-w-6xl rounded-lg shadow-lg text-white max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center border-b border-neutral-700 px-5 py-3">
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-yellow-400" /> Smart Metadata Detection
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-white">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-5 space-y-4">
                    <div className="bg-neutral-800 p-3 rounded text-sm text-gray-300 flex items-start gap-2">
                        <Info className="w-4 h-4 text-blue-400 mt-1" />
                        <span>
                            This tool helps analyze your data source and auto-generates rich metadata for each column, including inferred types, categories, and contextual descriptions. You can refine or override results before saving.
                        </span>
                    </div>

                    {step === 1 && (
                        <div className="space-y-4">
                            <label className="block text-sm text-gray-300">Optional Instructions</label>
                            <textarea
                                value={instructions}
                                onChange={(e) => setInstructions(e.target.value)}
                                className="w-full bg-neutral-800 px-3 py-2 rounded outline-none text-white text-sm"
                                rows={4}
                                placeholder="E.g. prioritize financial columns, or focus on timestamp fields..."
                            />
                            <button
                                onClick={loadMetadata}
                                className="bg-blue-600 hover:bg-blue-700 px-5 py-2 rounded text-sm"
                            >
                                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Load Metadata'}
                            </button>

                        </div>
                    )}

                    {step === 2 && (
                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <h3 className="text-md font-medium">Select Tables to Analyze</h3>
                                <button
                                    onClick={toggleSelectAll}
                                    className="text-sm text-indigo-400 hover:text-indigo-300 flex items-center gap-1"
                                >
                                    {selectedTables.length === tables.length ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                                    {selectedTables.length === tables.length ? 'Deselect All' : 'Select All'}
                                </button>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                {tables.map((table) => (
                                    <label key={table} className="flex items-center gap-2 bg-neutral-800 px-3 py-2 rounded cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={selectedTables.includes(table)}
                                            onChange={() => toggleTable(table)}
                                        />
                                        <span>{table}</span>
                                    </label>
                                ))}
                            </div>

                            <div className="flex items-center gap-3">
                                <button
                                    onClick={processMetadata}
                                    className="bg-indigo-600 hover:bg-indigo-700 px-4 py-2 rounded"
                                >
                                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Generate Metadata'}
                                </button>
                                <button
                                    onClick={() => setStep(1)}
                                    className="flex items-center text-sm text-yellow-400 hover:text-yellow-300"
                                >
                                    <RefreshCcw className="w-4 h-4 mr-1" /> Reload Metadata
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="space-y-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-md font-semibold text-white">Review and Edit Metadata</h3>

                                <button
                                    className="flex items-center gap-1 text-cyan-400 hover:text-cyan-300 text-sm"
                                    title="View Schema Diagram"
                                    onClick={() => setShowErdModal(true)}
                                >
                                    <TableProperties className="w-4 h-4" />
                                    View Schema ERD
                                </button>
                            </div>


                            <div className="overflow-x-auto">
                                {Object.entries(processedSchema).map(([table, cols]) => (
                                    <div key={table} className="mb-6">
                                        <h4 className="text-gray-300 font-semibold mb-2">{table}</h4>
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-sm bg-neutral-800 rounded">
                                                <thead>
                                                    <tr className="text-left text-gray-400">
                                                        <th className="px-2 py-1">Column</th>
                                                        <th className="px-2 py-1">Type</th>
                                                        <th className="px-2 py-1">Tag</th>
                                                        <th className="px-2 py-1">Description</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {cols.map(col => (
                                                        <tr key={col.name} className="border-t border-neutral-700">
                                                            <td className="px-2 py-1 text-white">{col.name}</td>
                                                            <td className="px-2 py-1 text-gray-400">{col.type}</td>
                                                            <td className="px-2 py-1">
                                                                <select
                                                                    value={col.tag || ''}
                                                                    onChange={(e) => updateColumnTag(table, col.name, 'tag', e.target.value)}
                                                                    className="bg-neutral-700 text-white px-2 py-1 rounded"
                                                                >
                                                                    <option value="">None</option>
                                                                    <option value="Identifier">Identifier</option>
                                                                    <option value="Metric">Metric</option>
                                                                    <option value="Time">Time</option>
                                                                    <option value="Category">Category</option>
                                                                    <option value="Description">Description</option>
                                                                </select>
                                                            </td>
                                                            <td className="px-2 py-1">
                                                                <input
                                                                    type="text"
                                                                    value={col.description || ''}
                                                                    onChange={(e) => updateColumnTag(table, col.name, 'description', e.target.value)}
                                                                    className="bg-neutral-700 text-white px-2 py-1 rounded w-full"
                                                                />
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <button onClick={() => alert('Save logic goes here')} className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded">
                                Save Metadata
                            </button>
                        </div>
                    )}
                    {ShowErdModal ? <ERDPreviewModal chart={chartCode} onClose={() => setShowErdModal(false)} /> : null}

                </div>
            </div>
        </div>
    );
};

export default SmartMetadataModal;
